// Minimal React error boundary used to surface JS errors inside the
// running app (especially useful for iOS TestFlight where the Safari
// Web Inspector isn't accessible). Renders the error message + stack
// + the most recent global error and gives the user a copy-to-clipboard
// button so they can paste the trace back.

import React from "react";

export default class HayahErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }
  componentDidMount() {
    this._onErr = (e) => {
      try {
        const msg = `[window.onerror] ${e?.message || e}`;
        localStorage.setItem("hayahLastError", `${msg}\n${e?.error?.stack || ""}`);
      } catch {}
    };
    this._onRej = (e) => {
      try {
        const reason = e?.reason;
        const msg = `[unhandledrejection] ${reason?.message || String(reason)}`;
        localStorage.setItem("hayahLastError", `${msg}\n${reason?.stack || ""}`);
      } catch {}
    };
    window.addEventListener("error", this._onErr);
    window.addEventListener("unhandledrejection", this._onRej);
  }
  componentWillUnmount() {
    if (this._onErr) window.removeEventListener("error", this._onErr);
    if (this._onRej) window.removeEventListener("unhandledrejection", this._onRej);
  }
  componentDidCatch(error, info) {
    try {
      localStorage.setItem(
        "hayahLastError",
        `[componentDidCatch] ${error?.message || error}\n${error?.stack || ""}\n${info?.componentStack || ""}`
      );
    } catch {}
    this.setState({ error, info });
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  copy = () => {
    const text = this._render();
    try {
      navigator.clipboard?.writeText(text);
    } catch {}
  };
  _render() {
    const { error, info } = this.state;
    let last = "";
    try { last = localStorage.getItem("hayahLastError") || ""; } catch {}
    return [
      `tab: ${this.props.label || "Hayah"}`,
      `where: ${this.props.where || "render"}`,
      "",
      `error: ${error?.message || error || "(none)"}`,
      "",
      "stack:",
      error?.stack || "(no stack)",
      "",
      "componentStack:",
      info?.componentStack || "(none)",
      "",
      "last global error (window.onerror / unhandledrejection):",
      last || "(none)",
    ].join("\n");
  }
  render() {
    if (!this.state.error) return this.props.children;
    const text = this._render();
    return (
      <div style={{
        minHeight: "100%", padding: "calc(20px + env(safe-area-inset-top)) 16px 80px",
        background: "#1a0707", color: "#ffdede", fontFamily: "ui-monospace, monospace",
        fontSize: 11, lineHeight: 1.5, overflow: "auto",
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: "#ff8c8c", letterSpacing: 1 }}>
          ⚠ HAYAH CRASHED
        </div>
        <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0 }}>{text}</pre>
        <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
          <button onClick={this.copy} style={{
            padding: "10px 14px", borderRadius: 8, border: "1px solid #ff8c8c",
            background: "#330e0e", color: "#ffdede", fontFamily: "inherit", fontSize: 11, fontWeight: 700,
            letterSpacing: 1, cursor: "pointer", textTransform: "uppercase",
          }}>Copy to clipboard</button>
          <button onClick={() => this.setState({ error: null, info: null })} style={{
            padding: "10px 14px", borderRadius: 8, border: "1px solid #ffdede",
            background: "transparent", color: "#ffdede", fontFamily: "inherit", fontSize: 11, fontWeight: 700,
            letterSpacing: 1, cursor: "pointer", textTransform: "uppercase",
          }}>Dismiss</button>
        </div>
      </div>
    );
  }
}
